// What closes a stop on the path: an empty frame until the stop is done, then
// its Polaroid, whether or not this device remembers taking it.
import type { Taken } from "@aybuben/client/device/polaroid-memory.ts";
import { photoDate } from "@aybuben/client/places/photo.ts";
import type { Stop, StopState } from "@aybuben/client/places/stops.ts";
import type { LangId } from "@aybuben/core/lang.ts";

import { useStrings } from "../../i18n/index.tsx";
import { COL, edgeOf, FOCUS } from "../../theme.ts";
import { PolaroidSlot } from "./art.tsx";

interface PhotoSpotProps {
	stop: Stop;
	state: StopState;
	taken: Taken | null;
	lang: LangId;
	onOpen: () => void;
}

export function PhotoSpot({ stop, state, taken, lang, onOpen }: PhotoSpotProps) {
	const t = useStrings();
	const name = stop.place.name;
	if (state !== "done")
		return (
			<div
				data-testid="photo-spot"
				role="img"
				aria-label={t("place.photoSpotAria", { name })}
				className="mx-auto mt-7 flex flex-col items-center"
			>
				<div
					style={{
						width: 130,
						padding: "9px 9px 28px",
						border: `2px dashed ${COL.greyEdge}`,
						borderRadius: 4,
						transform: "rotate(-3deg)",
					}}
				>
					<div
						aria-hidden
						className="flex items-center justify-center"
						style={{ height: 112, background: COL.tint, fontSize: 28 }}
					>
						📷
					</div>
				</div>
				<p className="mt-3 text-center text-sm" style={{ color: COL.mute, maxWidth: 220 }}>
					{t("place.photoSpot", { name })}
				</p>
			</div>
		);
	const date = photoDate(taken, lang);
	return (
		<button
			type="button"
			data-testid="photo-spot"
			onClick={onOpen}
			aria-label={
				date ? t("homeSheet.photo", { name, date }) : t("homeSheet.photoUndated", { name })
			}
			className={`relative mx-auto mt-7 block ${FOCUS}`}
		>
			<PolaroidSlot stop={stop} taken={taken} lang={lang} size={130} tilt={-3} />
			<span
				aria-hidden
				className="rounded-full"
				style={{
					position: "absolute",
					top: -7,
					left: "50%",
					width: 12,
					height: 12,
					marginLeft: -6,
					background: COL.pom,
					boxShadow: `0 2px 0 ${edgeOf(COL.pom)}`,
				}}
			/>
		</button>
	);
}
